/**
 * Helpers for phased model download progress (build → browser download / ECR push).
 */

/** Map backend 0–100 ECR progress into build (0–50) + push (50–100) UI steps. */
export function mapEcrOverallProgress(overallProgress) {
    const p = Math.max(0, Math.min(100, Math.round(overallProgress)));
    if (p >= 50) {
        return {
            phase: 'pushing',
            buildProgress: 100,
            downloadProgress: Math.min(100, (p - 50) * 2),
        };
    }
    return {
        phase: 'building',
        buildProgress: Math.min(100, p * 2),
        downloadProgress: 0,
    };
}

export function usesPhasedDownloadUi(file) {
    if (!file?.isDownload) {
        return false;
    }
    if (
        file.artifactType === 'ecr' ||
        file.artifactType === 'gcr' ||
        file.artifactType === 'dockerhub'
    ) {
        return true;
    }
    return (
        file.phase != null ||
        file.artifactType === 'docker' ||
        typeof file.buildProgress === 'number' ||
        typeof file.downloadProgress === 'number'
    );
}

export function isDownloadProgressActive(file) {
    if (!file?.isDownload || file.isError) {
        return false;
    }
    if (file.phase === 'downloading') {
        if (file.downloadIndeterminate) {
            return true;
        }
        return (file.downloadProgress ?? 0) < 100;
    }
    if (file.phase === 'building') {
        return (file.buildProgress ?? file.progress ?? 0) < 100;
    }
    if (file.phase === 'pushing') {
        return (file.downloadProgress ?? 0) < 100;
    }
    if (file.phase === 'ready_to_save') {
        // Build is finished but the actual save-to-disk is gated on a user click.
        // Treat as active so the FloatingProgressBar stays visible and the
        // auto-hide timeout is suppressed until the user picks a destination.
        return true;
    }
    if (file.phase === 'complete') {
        return false;
    }
    return !file.isCompleted && (file.progress ?? 0) > 0 && (file.progress ?? 0) < 100;
}

export function isDownloadProgressComplete(file) {
    if (!file?.isDownload || file.isError) {
        return false;
    }
    if (file.localFilePath) {
        return true;
    }
    if (file.phase === 'complete') {
        return true;
    }
    if (file.isCompleted && file.phase !== 'downloading' && file.phase !== 'building') {
        return true;
    }
    return Boolean(file.isCompleted) || file.progress === 100;
}

/**
 * @param {object} file Progress status entry with optional phase / buildProgress / downloadProgress.
 */
export function deriveDownloadSteps(file) {
    const buildPct = Math.max(
        0,
        Math.min(
            100,
            Math.round(
                typeof file.buildProgress === 'number'
                    ? file.buildProgress
                    : file.phase === 'downloading' ||
                        file.phase === 'pushing' ||
                        file.phase === 'ready_to_save' ||
                        file.phase === 'complete'
                      ? 100
                      : file.progress ?? 0
            )
        )
    );

    const downloadPct = Math.max(
        0,
        Math.min(100, Math.round(typeof file.downloadProgress === 'number' ? file.downloadProgress : 0))
    );

    const isEcr = file.artifactType === 'ecr';
    const isGcr = file.artifactType === 'gcr';
    const isDockerHub = file.artifactType === 'dockerhub';
    const isRegistryPush = isEcr || isGcr || isDockerHub;

    const buildLabel = isRegistryPush
        ? file.phase === 'building' && file.message
            ? file.message
            : 'Building inference image'
        : file.artifactType === 'docker'
            ? file.phase === 'building' && file.message
                ? file.message
                : 'Building Docker image'
            : file.message || 'Preparing download';

    const downloadLabel = isEcr
        ? file.phase === 'pushing' && file.message
            ? file.message
            : 'Pushing to ECR'
        : isGcr
            ? file.phase === 'pushing' && file.message
                ? file.message
                : 'Pushing to GCR'
            : isDockerHub
                ? file.phase === 'pushing' && file.message
                    ? file.message
                    : 'Pushing to Docker Hub'
                : file.phase === 'ready_to_save'
                    ? (file.artifactType === 'docker'
                        ? 'Image ready - save to disk'
                        : 'Ready - save to disk')
                    : file.downloadIndeterminate
                        ? 'Downloading via browser…'
                        : 'Downloading to your computer…';

    let buildState = 'pending';
    if (file.isError && file.phase === 'building') {
        buildState = 'error';
    } else if (buildPct >= 100 && !file.isError) {
        buildState = 'complete';
    } else if (file.phase === 'building' || (buildPct > 0 && buildPct < 100)) {
        buildState = 'active';
    }

    let downloadState = 'pending';
    if (file.isError && (file.phase === 'downloading' || file.phase === 'pushing' || buildPct >= 100)) {
        downloadState = 'error';
    } else if (file.phase === 'complete' || downloadPct >= 100) {
        downloadState = 'complete';
    } else if (file.phase === 'ready_to_save') {
        // Build complete; second step is awaiting the user's "Save to disk" click.
        // Render as ``pending`` so the indicator dot is unfilled — the action
        // button replaces the percent and signals what to do next.
        downloadState = 'pending';
    } else if (
        file.phase === 'downloading' ||
        file.phase === 'pushing' ||
        file.downloadIndeterminate ||
        (downloadPct > 0 && downloadPct < 100)
    ) {
        downloadState = 'active';
    }

    return {
        buildState,
        downloadState,
        buildLabel,
        downloadLabel,
        buildPercent: buildPct,
        downloadPercent: downloadPct,
        downloadIndeterminate: Boolean(file.downloadIndeterminate),
        useTwoStep: usesPhasedDownloadUi(file),
    };
}

/** Partial-success terminal state (e.g. some examples blocked by content filter). */
export function isProgressFileWarning(file) {
    return Boolean(file.isWarning) && !file.isError;
}

export function isProgressFileActive(file) {
    if (file.isError || file.isWarning) {
        return false;
    }
    if (file.isDownload) {
        return isDownloadProgressActive(file);
    }
    if (file.isGeneration || file.isRetrievalAgent) {
        return !file.isCompleted && !file.isError;
    }
    // A server-side analysis tick sits at 0% until the import starts.
    if (file.ingestPhase && !file.isCompleted) {
        return true;
    }
    return (file.progress ?? 0) > 0 && (file.progress ?? 0) < 100 && !file.isCompleted;
}

export function isProgressFileComplete(file) {
    if (file.isError) {
        return false;
    }
    // A warning entry is terminal-but-visible: treat it like a completed item
    // so it is not counted as active and stays on screen for the user to read.
    if (file.isWarning) {
        return true;
    }
    if (file.isDownload) {
        return isDownloadProgressComplete(file);
    }
    if (file.isGeneration || file.isRetrievalAgent) {
        return Boolean(file.isCompleted);
    }
    return file.progress === 100 || file.isCompleted;
}
